/**
 * Shared display formatters — the single source of truth for how money,
 * dates, and table-header casing are rendered anywhere in the app.
 *
 * See docs/design-language/DISPLAY-CONSISTENCY-PRD.md. A feature author passes
 * data in; these own the presentation. If you find yourself writing `toFixed`,
 * `new Date(...).toLocale*`, or a hand-rolled relative-time helper, stop — it
 * lives here.
 *
 * Rules:
 *  - Money is ALWAYS exactly 2 fraction digits (1.9001 → "$1.90"). No variants.
 *  - Dates never show seconds. `2026-05-28 23:08:07` is not acceptable output.
 *  - Every formatter is total: null/invalid input → a dash, never "Invalid Date".
 */

const DASH = "—";

/**
 * Parse a value that may be a SQLite datetime ("YYYY-MM-DD HH:MM:SS", stored in
 * UTC with no zone suffix), an ISO-8601 string, an epoch-ms number, or a Date.
 * Returns null for null/undefined/empty/unparseable input.
 *
 * The SQLite shape is normalized to UTC by inserting the "T" separator and a
 * "Z" suffix — this is the one place that knowledge lives (it was previously
 * copy-pasted, and inconsistently, across four call sites).
 */
function parseDate(
  value: string | number | Date | null | undefined,
): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = value.trim();
  if (!raw) return null;
  // Date-only "YYYY-MM-DD" (a calendar day, e.g. a due date) → local midnight,
  // NOT UTC. `new Date("2026-06-04")` parses as UTC midnight, which renders as
  // the PREVIOUS day in any timezone west of UTC — so a due date would shift
  // back a day. Construct it in local time so the calendar day is preserved.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    const local = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(local.getTime()) ? null : local;
  }
  // SQLite "YYYY-MM-DD HH:MM:SS" (space-separated, no zone) → treat as UTC.
  const sqlite = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(raw);
  const normalized = sqlite
    ? raw.replace(" ", "T") + (raw.endsWith("Z") ? "" : "Z")
    : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Money. Always 2 fraction digits, grouped, `$` prefix. 1.9001 → "$1.90".
 * Sub-cent values read as "$0.00" — there is deliberately no precise variant
 * (PRD §4). null/undefined → the dash (override via opts.dash).
 */
export function formatUsd(
  amount: number | null | undefined,
  opts?: { dash?: string },
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return opts?.dash ?? DASH;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Date only → "May 28, 2026". null/invalid → dash. */
export function formatDate(
  value: string | number | Date | null | undefined,
): string {
  const d = parseDate(value);
  if (!d) return DASH;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Compact ledger date → "Jul 20" (this year) / "Jul 20, 25" (any other year).
 * The list-row form of `formatDate`: a fixed-width, always-a-date stamp for the
 * date column of a `LifecycleRow` — never a relative "5d ago", which is both
 * variable-width and imprecise (Matt, 2026-07-20: "just have the date like
 * 'Jul 20' and should always have that formatted"). The off-year suffix is
 * two-digit so the whole scale still fits one fixed column. null/invalid → dash.
 */
export function formatDayMonth(
  value: string | number | Date | null | undefined,
): string {
  const d = parseDate(value);
  if (!d) return DASH;
  const stamp = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const year = d.getFullYear();
  if (year === new Date().getFullYear()) return stamp;
  return `${stamp}, ${String(year).slice(-2)}`;
}

/** Date + time, no seconds → "May 28, 2026, 11:08 PM". null/invalid → dash. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
): string {
  const d = parseDate(value);
  if (!d) return DASH;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Relative time → "just now" | "13m ago" | "3h ago" | "5d ago"; falls back to
 * `formatDate` for anything older than a week. null/invalid → dash.
 */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
): string {
  const d = parseDate(value);
  if (!d) return DASH;
  const seconds = Math.round((Date.now() - d.getTime()) / 1000);
  if (seconds < 0) return formatDate(d);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;
  return formatDate(d);
}

/**
 * Plain count → grouped integer string ("1234.5" → "1,235", 1234 → "1,234").
 * Rounds to the nearest whole number by default; pass `maxFractionDigits` for a
 * decimal count. null/undefined/NaN → the dash (override via opts.dash). Use
 * this for any raw integer that reaches the UI instead of `String(n)` or a
 * hand-rolled `Intl.NumberFormat`.
 */
export function formatNumber(
  value: number | null | undefined,
  opts?: { maxFractionDigits?: number; dash?: string },
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return opts?.dash ?? DASH;
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: opts?.maxFractionDigits ?? 0,
  }).format(value);
}

/**
 * Compact magnitude → "1.2k" | "3.4M" | "5.6B"; anything under 1,000 renders as
 * a plain grouped integer. One fraction digit on the suffixed forms. Negatives
 * keep their sign. null/invalid → the dash. Supersedes the copy-pasted
 * `fmtTokens`-style helpers (PRD §4.1/§4.4).
 */
export function formatCompact(
  value: number | null | undefined,
  opts?: { dash?: string },
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return opts?.dash ?? DASH;
  }
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}k`;
  return `${sign}${formatNumber(abs)}`;
}

/**
 * Wall duration in milliseconds → "812ms" | "13s" | "3m 4s" | "2h 15m".
 * null/invalid/negative → dash. Used by session metrics (SI-3-04) and any
 * surface that holds a measured interval rather than a wall-clock instant
 * (prefer `formatRelativeTime` for instants).
 */
export function formatDurationMs(
  ms: number | null | undefined,
  opts?: { dash?: string },
): string {
  const dash = opts?.dash ?? DASH;
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms < 0) return dash;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  if (min < 60) return rem === 0 ? `${min}m` : `${min}m ${rem}s`;
  const hr = Math.floor(min / 60);
  const mrem = min % 60;
  return mrem === 0 ? `${hr}h` : `${hr}h ${mrem}m`;
}

/**
 * Human file size → "812 B" | "3.4 KB" | "5.1 MB" | "2.0 GB" (1024-based, one
 * fraction digit above KB, none for bytes). null/invalid/negative → the dash.
 * The single shared formatter for `attachments.byte_size` and the like.
 */
export function formatBytes(
  value: number | null | undefined,
  opts?: { dash?: string },
): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) {
    return opts?.dash ?? DASH;
  }
  if (value < 1024) return `${Math.round(value)} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let n = value / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${units[i]}`;
}

/**
 * Capitalize only the first letter of a label — used by the table primitives so
 * a column header passed as `"run"` renders `"Run"` (PRD §6). First-letter
 * only, never title-case: "cost (24h)" → "Cost (24h)", "Last activity" → unchanged.
 */
export function capitalizeFirst(label: string): string {
  if (!label) return label;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Title-case a human-or-slug label: split on whitespace/underscore/hyphen and
 * upper-case each word's first letter. "go-to-market" → "Go To Market",
 * "acme corp" → "Acme Corp". Used for project/entity names that may arrive
 * lower-cased or slug-shaped. (Promoted from a private copy in project-chrome.)
 */
export function titleCase(input: string): string {
  return input
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => (w[0]?.toUpperCase() ?? "") + w.slice(1))
    .join(" ");
}

/** The owner sentinels a strategic / shell-created goal carries in place of an
 *  agent id (`objectives.ts` STRATEGIC_OWNER, `goal-create.ts` shell path). Both
 *  mean "a person owns this", so both read as "Person". */
const PERSON_OWNER_SENTINELS = new Set(["human", "human-shell"]);

/**
 * The ONE display-boundary humanizer for an agent/goal role or owner sentinel.
 * These values (`excellent_goals.role` / `owner_agent_id`, `worker_runs.role`)
 * are enum-like tokens stored raw and branched on in logic — never display text
 * — so they humanize HERE, exactly like a status/kind enum, and never in the DB.
 * The person-owner sentinels ("human", "human-shell") read as "Person"; a machine
 * role word ("shipper") title-cases ("Shipper"). Lane/prose labels pass through.
 */
export function roleLabel(role: string): string {
  if (PERSON_OWNER_SENTINELS.has(role)) return "Person";
  return titleCase(role);
}

/** A derived, process-tree agent id — `claude-fbb7d0`, `codex-8df0`,
 *  `human-921609` — that must never reach the UI raw. */
const AGENT_ID_RE = /^[a-z][a-z0-9]*[-_][0-9a-f]{4,}$/i;

/**
 * Humanize an agent id to its host name for display — the ONE such transform,
 * shared by the app's `friendlyAgentName`/`agentLabel` and by packages/ui blocks
 * (the worker-runs feed) so no surface leaks the raw id. `claude-fbb7d0` →
 * "Claude"; the human-operator host reads "Person" (the same rename the goal
 * owner sentinel gets); a string that isn't id-shaped (a real name) passes
 * through untouched. The raw id stays the identity in the data plane — this is
 * display-only.
 */
export function agentDisplayName(id: string): string {
  if (!AGENT_ID_RE.test(id)) return id;
  const host = id.split(/[-_]/)[0] ?? id;
  if (host.toLowerCase() === "human") return "Person";
  return host.charAt(0).toUpperCase() + host.slice(1);
}
